// Hydratation — objectif d'eau quotidien (suggestion ajustable) et suivi.
// Repère courant : ~30–40 ml/kg/jour.

import { aggregateBy } from './aggregate';
import { todayKey } from './datetime';
import { clamp, mean, round } from './statistics';
import type { WaterEntry } from './types';

// Bornes du réglage ml/kg (garde-fous UI + calcul).
export const WATER_ML_PER_KG = { min: 20, max: 60, default: 35 } as const;

// Objectif d'eau quotidien (ml) = poids × ml/kg (borné à [min, max]).
export function suggestWaterTarget(
  weightKg: number,
  mlPerKg: number = WATER_ML_PER_KG.default,
): number {
  if (weightKg <= 0) return NaN;
  const perKg = clamp(mlPerKg, WATER_ML_PER_KG.min, WATER_ML_PER_KG.max);
  // Arrondi aux 50 ml.
  return Math.round((weightKg * perKg) / 50) * 50;
}

export function dailyWater(entries: readonly WaterEntry[]): { date: string; ml: number }[] {
  return aggregateBy(
    entries,
    (e) => e.datetime,
    'day',
    (group) => group.reduce((sum, e) => sum + e.ml, 0),
  ).map((b) => ({ date: b.start, ml: b.value }));
}

export interface WaterStats {
  today: number;
  daysLogged: number;
  daysMet: number;
  adherencePct: number;
  currentStreak: number;
  bestStreak: number;
  avgMl: number;
}

// Un jour « atteint » = total ≥ objectif.
export function waterStats(
  entries: readonly WaterEntry[],
  targetMl: number,
  today: string = todayKey(),
): WaterStats {
  const daily = dailyWater(entries);
  const met = daily.map((d) => d.ml >= targetMl);

  let best = 0;
  let running = 0;
  for (const ok of met) {
    running = ok ? running + 1 : 0;
    if (running > best) best = running;
  }
  let current = 0;
  for (let i = met.length - 1; i >= 0 && met[i]; i--) current++;

  const daysMet = met.filter(Boolean).length;
  return {
    today: daily.find((d) => d.date === today)?.ml ?? 0,
    daysLogged: daily.length,
    daysMet,
    adherencePct: daily.length ? round((daysMet / daily.length) * 100, 1) : 0,
    currentStreak: current,
    bestStreak: best,
    avgMl: daily.length ? Math.round(mean(daily.map((d) => d.ml))) : 0,
  };
}
